<script lang="ts">
    import AttributeAnswers from "@apps/reviews/components/AttributeAnswers/AttributeAnswers.svelte";
    import ReviewRatingStars from "@apps/reviews/components/RatingStars/ReviewRatingStars.svelte";
    import ReviewReply from "@apps/reviews/components/ReviewsList/ListingLayout/ReviewReply/ReviewReply.svelte";
    import ReviewsMeta from "@apps/reviews/components/ReviewsMeta.svelte";
    import ReviewsTranslateButton from "@apps/reviews/components/ReviewsTranslateButton.svelte";
    import ToggleVote from "@apps/reviews/components/ToggleVote.svelte";
    import { REVIEWS_REPLY_STATUS } from "@apps/reviews/constants";
    import ReviewEntity from "@apps/reviews/entities/ReviewEntity";
    import { ReviewTranslationResponseEntity } from "@apps/reviews/entities/ReviewTranslationEntity";
    import { VotesState } from "@apps/reviews/stores/VotesStore";
    import { getHumanReadableDate } from "@lib/utils";
    import { I18nModule } from "@modules/i18n/I18nModule";
    import { BehaviorSubject } from "rxjs";
    import { createEventDispatcher, onMount } from "svelte";
    import { fly } from "svelte/transition";
    import { container } from "tsyringe";

    import Icon from "@components/Icon.svelte";
    import InfoIcon from "@components/icons/InfoIcon.svelte";
    import Scrollable from "@components/Scrollable.svelte";
    import T from "@components/T.svelte";
    import TooltipActivator from "@components/Tooltip/TooltipActivator.svelte";

    export let reviewsWithTitleEnabled: boolean;
    export let review: ReviewEntity;
    export let reviewVotes: VotesState | undefined;
    export let reviewTranslation: ReviewTranslationResponseEntity | null;
    export let translationsPendingIds: number[];
    export let translationEnabled: boolean;
    export let showTranslation$: BehaviorSubject<boolean>;
    export let isIncentivizedEnabled: boolean;

    let scrollPosition = 0;
    let isScrollable = false;
    let scrollableAreaRef: HTMLDivElement;
    let scrollableContentRef: HTMLDivElement;
    const dispatch = createEventDispatcher();
    const i18nModule = container.resolve(I18nModule);
    const t_anonym$ = i18nModule.exports.i18n.selectT(
        "reviews.ReviewsGalleryWidget",
        "anonym"
    );

    const handleScroll = (event: Event) => {
        if (event.currentTarget instanceof HTMLDivElement) {
            scrollPosition = event.currentTarget.scrollTop;
        }
    };

    onMount(() => {
        if (!scrollableAreaRef || !scrollableContentRef) return;

        // Observes the resize of the scrollable area
        const resizeObserver = new ResizeObserver(() => {
            const scrollable =
                scrollableAreaRef?.querySelector(".gw-scrollable");
            isScrollable = !!(
                scrollable && scrollable.clientHeight < scrollable.scrollHeight
            );
        });

        resizeObserver.observe(scrollableContentRef);

        return () => {
            resizeObserver.disconnect();
        };
    });

    const handleSeeTranslation = (
        event: CustomEvent<{ reviewId: number; targetLanguage: string }>
    ) => {
        showTranslation$.next(!$showTranslation$);

        if (!isShowTranslation) {
            dispatch("seeTranslation", {
                reviewId: event.detail.reviewId,
                targetLanguage: event.detail.targetLanguage,
            });
        }
    };

    $: isShowTranslation = $showTranslation$;

    $: isTranslationLoading = translationsPendingIds.includes(review.id);

    $: isTranslated = isShowTranslation && !isTranslationLoading;

    $: getContent = () => {
        if (isTranslated) {
            return {
                title: reviewTranslation?.title,
                body: reviewTranslation?.body,
                reply: reviewTranslation?.reply,
            };
        }
        return {
            title: review.title,
            body: review.body,
            reply: review.reply?.body,
        };
    };
    $: content = getContent();

    $: customerName = review.customer?.name ?? $t_anonym$;
</script>

<div class="gw-rv-gallery-content">
    <div class="gw-rv-gallery-content__body">
        <!-- Author info -->
        <div class="gw-rv-gallery-content__wrapper">
            <div class="gw-rv-gallery-content__author">
                <span class="gw-rv-gallery-content__author-name">
                    {customerName}
                </span>
                <ReviewsMeta
                    {review}
                    namespace="reviews.ReviewsGalleryWidget"
                />
            </div>

            {#if translationEnabled && review.isPublished}
                <div class="gw-rv-gallery-content__translation">
                    <ReviewsTranslateButton
                        namespace="reviews.ReviewsGalleryWidget"
                        {review}
                        showTranslation={isShowTranslation}
                        {isTranslationLoading}
                        on:seeTranslation={handleSeeTranslation}
                    />
                </div>
            {/if}
        </div>

        <!-- Rating and date -->
        <div class="gw-rv-gallery-content__rating-date-block">
            <ReviewRatingStars
                width={"20"}
                height={"20"}
                rating={review.rating}
            />
            <span class="gw-rv-gallery-content__date"
                >{getHumanReadableDate(review.createdAt)}</span
            >
        </div>

        <!-- Scrollable content area -->
        <div
            class="gw-rv-gallery-content__review-scrollable-area"
            bind:this={scrollableAreaRef}
        >
            <Scrollable
                class="gw-rv-gallery-content__scrollable"
                on:scroll={handleScroll}
                bottomShadow
                topShadow
                visible
            >
                <div
                    class="gw-rv-gallery-content__content"
                    bind:this={scrollableContentRef}
                >
                    <div class="gw-rv-gallery-content__content__inner">
                        {#if (reviewsWithTitleEnabled && review.title) || (review.isIncentivized && isIncentivizedEnabled)}
                            <div class="gw-rv-gallery-content__title-bar">
                                <!-- Review title -->
                                {#if reviewsWithTitleEnabled && review.title}
                                    <h4
                                        class="gw-rv-gallery-content__review-title"
                                    >
                                        {content.title || ""}
                                    </h4>
                                {/if}

                                {#if isIncentivizedEnabled && review.isIncentivized}
                                    <div
                                        class="gw-rv-gallery-content__incentivized-wrapper"
                                    >
                                        <TooltipActivator
                                            position="bottom-end"
                                            maxWidth="308px"
                                        >
                                            <div
                                                class="gw-rv-gallery-content__incentivized"
                                            >
                                                <T
                                                    namespace="reviews.ReviewsGalleryWidget"
                                                    translationKey="incentivized"
                                                />

                                                <Icon
                                                    width="14"
                                                    height="14"
                                                    color="#12b76a"
                                                    stroke="#12b76a"
                                                >
                                                    <InfoIcon />
                                                </Icon>
                                            </div>

                                            <T
                                                slot="content"
                                                namespace="reviews.ReviewsGalleryWidget"
                                                translationKey="incentivized_content"
                                            />
                                        </TooltipActivator>
                                    </div>
                                {/if}
                            </div>
                        {/if}

                        <!-- Review body -->
                        <div class="gw-rv-gallery-content__review">
                            <p class="gw-rv-gallery-content__review-body">
                                {content.body || ""}
                            </p>

                            {#if review.customerAttributeAnswers.length > 0 || review.productAttributeAnswers.length > 0}
                                <div class="gw-rv-gallery-content__attributes">
                                    <!-- Customer attributes answers -->
                                    {#if review.customerAttributeAnswers.length > 0}
                                        <AttributeAnswers
                                            attributeAnswers={review.customerAttributeAnswers}
                                        />
                                    {/if}

                                    <!-- Product attributes answers -->
                                    {#if review.productAttributeAnswers.length > 0}
                                        <AttributeAnswers
                                            attributeAnswers={review.productAttributeAnswers}
                                        />
                                    {/if}
                                </div>
                            {/if}

                            <!-- Admin reply -->
                            {#if review.reply && review.reply.status === REVIEWS_REPLY_STATUS.PUBLIC}
                                <div
                                    class="gw-rv-gallery-content__review-reply"
                                >
                                    <ReviewReply
                                        namespace="reviews.ReviewsGalleryWidget"
                                        reply={content.reply || ""}
                                    />
                                </div>
                            {/if}
                        </div>
                    </div>

                    <!-- Vote button -->
                    {#if reviewVotes}
                        <div class="gw-rv-gallery-content__vote">
                            <ToggleVote {reviewVotes} on:toggleVote />
                        </div>
                    {/if}
                </div>
            </Scrollable>

            <!-- Fixed vote button -->
            {#if scrollPosition === 0 && isScrollable && reviewVotes}
                <div
                    in:fly={{ y: 50, duration: 400 }}
                    out:fly={{ y: 50, duration: 400 }}
                    class="gw-rv-gallery-content__vote gw-rv-gallery-content__vote-fixed"
                >
                    <ToggleVote {reviewVotes} on:toggleVote />
                </div>
            {/if}
        </div>
    </div>

    <!-- Footer -->
    <div class="gw-rv-gallery-content__footer">
        <slot name="footer" />
    </div>
</div>

<style lang="postcss">
    /* General styles */
    .gw-rv-gallery-content {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        position: relative;
        height: 100%;
        padding-right: 0.2em;

        @media (max-width: 600px) {
            padding-bottom: 130px;
            padding-right: 0em;
        }
    }

    /* Body styles */
    .gw-rv-gallery-content__body {
        padding: 1.625em 1.25em 1em 1.625em;
        flex-grow: 1;
        height: 1px;
        display: flex;
        flex-direction: column;

        @media screen and (max-width: 1000px) {
            padding: 1.5em 1em 0;
        }

        @media screen and (max-width: 600px) {
            height: auto;
            padding: 1em 1em 0;
        }
    }

    .gw-rv-gallery-content__wrapper {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-top: 2.2em;

        @media screen and (max-width: 1000px) {
            padding-top: 0;
        }
    }

    /* Author styles */
    .gw-rv-gallery-content__author {
        display: flex;
        flex-wrap: wrap;
        gap: 0.375em;

        @media screen and (min-width: 1000px) {
            padding-right: 1.625em;
        }
    }

    /* Translation styles */
    .gw-rv-gallery-content__translation {
        display: flex;
        justify-content: end;
        font-size: 0.75em;
        padding-right: 0.25em;
    }

    /* Rating and date block styles */
    .gw-rv-gallery-content__rating-date-block {
        margin-top: 0.75em;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-right: 0.25em;

        @media (max-width: 600px) {
            margin-top: 0.2em;
        }
    }

    /* Date styles */
    .gw-rv-gallery-content__date {
        font-size: 0.75em;
        color: rgba(102, 102, 102, 1);
    }

    /* Scrollable area styles */
    .gw-rv-gallery-content__review-scrollable-area {
        margin-top: 0.875em;
        flex-grow: 1;
        height: 1px;
        overflow: hidden;
        position: relative;

        @media screen and (max-width: 600px) {
            height: auto;
        }
    }

    /* Review content styles */
    .gw-rv-gallery-content__content {
        padding-right: 0.25em;
    }

    .gw-rv-gallery-content__title-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: nowrap;
        gap: 0.75rem;
        margin-bottom: 0.5em;
    }

    /* Review title styles */
    .gw-rv-gallery-content__review-title {
        width: 100%;
        margin: 0;
        font-size: 1em;
        font-weight: 600;
    }

    /* Review incentivized styles */
    .gw-rv-gallery-content__incentivized-wrapper {
        display: flex;
        justify-content: flex-end;
        flex: 1;
    }

    .gw-rv-gallery-content__incentivized {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        font-size: 0.875em;
        font-style: normal;
        font-weight: 400;
        line-height: 1.4;
        cursor: pointer;
        color: var(--gw-lightgrey-text-color, #8a96a1);
    }

    /* Review body styles */
    .gw-rv-gallery-content__review-body {
        font-size: 0.875em;
        padding: 0;
        margin: 0;
    }

    /* Custom forms styles */
    .gw-rv-gallery-content__attributes {
        display: flex;
        flex-direction: column;
        gap: 1em;
        padding: 0.5em;
        margin-bottom: 1em;
        margin-top: 1em;
        background-color: #f6f6f6;
        border-radius: 2px;
    }

    /* Review reply styles */
    .gw-rv-gallery-content__review-reply {
        margin-top: 1em;

        @media screen and (max-width: 600px) {
            margin-top: 0.5em;
        }
    }

    /* Vote button styles */
    .gw-rv-gallery-content__vote {
        margin-top: 1.25em;
        display: flex;
        justify-content: end;

        &-fixed {
            background-color: #fff;
            border-radius: 6.25em;
            position: absolute;
            right: 1.625em;
            bottom: 0.5em;
            z-index: 10;

            @media screen and (max-width: 1000px) {
                right: 0.5em;
            }
        }
    }

    /* Footer styles */
    .gw-rv-gallery-content__footer {
        padding: 0 1em;
        @media (max-width: 600px) {
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100%;
            box-sizing: border-box;
            background-color: #fff;
        }
    }

    /* Responsive scrollable styles */
    :global(.gw-scrollable.gw-rv-gallery-content__scrollable) {
        @media screen and (max-width: 600px) {
            overflow-y: auto;

            &::after {
                z-index: -100;
            }
        }
    }
</style>
